import React from 'react'
import { FormattedMessage } from 'react-intl'
import { Row, Col } from '@gympass/yoga'
import { Download } from '@gympass/yoga-icons'
import tokens from '@gympass/yoga-tokens'
import { Img2d05 } from '@gympass/yoga-illustrations'

import useContact from '@hooks/useContact'
import useDataLayer from '@hooks/useDataLayer'
import useInternationalization from '@hooks/useInternationalization'

import defaultReportNameByCountry from '@client/mapper/defaultReportNameByCountry'

import { Facebook, Instagram, Youtube } from '@assets/icons'

import Appbar from '@components/Appbar'
import Footer from '@components/Footer'
import YelpPixel from '@components/YelpPixel'

import getCommercialOfferPdf from '@client/services/print'

import SummaryCard from './components/SummaryCard'

import * as Style from './Success.style'

export default function Success() {
  const { country } = useInternationalization()
  const { events } = useDataLayer()
  const { contact } = useContact()

  const links = [
    {
      url: 'https://www.gympass.com/us',
      label: 'smb_acquisition_footer.call_to_action.home',
    },
  ]

  const social = [
    {
      url: 'https://www.facebook.com/GympassUS',
      icon: Facebook,
      alt: 'facebook-link',
    },
    {
      url: 'https://www.instagram.com/gympass_usa',
      icon: Instagram,
      alt: 'instagram-link',
    },
    {
      url: 'https://www.youtube.com/channel/UCTNRunlqGLZQK8HNRDze6bg',
      icon: Youtube,
      alt: 'youtube-link',
    },
  ]

  function handleDownloadFile(file) {
    const url = window.URL.createObjectURL(new Blob([file]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute(
      'download',
      `${defaultReportNameByCountry[country]}-${contact.company}.pdf`
    )
    document.body.appendChild(link)
    link.click()
  }

  async function getCommercialOfferFile() {
    const discount =
      contact.proposalValue - contact.proposalValueWithoutDiscount

    const data = {
      personalInfo: {
        company: contact.company,
        fullName: `${contact.firstName} ${contact.lastName}`,
        email: contact.email,
        phone: contact.mobilePhone,
      },
      companyInfo: {
        employeesNumber: contact.employeesNumber,
      },
      commercialOffer: {
        originalValue: contact.proposalValueWithoutDiscount,
        value: contact.proposalValue,
        discount,
      },
    }

    const response = await getCommercialOfferPdf(data, country)
    handleDownloadFile(response.data)

    events.interactionWithProposalDownload()
  }

  return (
    <>
      <Appbar />

      <Style.Header>
        <Style.Container>
          <Style.WrapperTitle>
            <Col xxs={12} xl-start={2} xl={5}>
              <Img2d05
                width={230}
                height={230}
                role="figure"
                aria-label="i2s-settings-modal"
              />
              <Style.Title>
                <FormattedMessage id="smb_acquisition_thank_you.success.title" />
              </Style.Title>

              <Style.Subtitle>
                <FormattedMessage
                  id="smb_acquisition_thank_you.success.subtitle"
                  values={{
                    contactEmail: contact.email,
                    strong: (...chunks) => <strong>{chunks}</strong>,
                  }}
                />
              </Style.Subtitle>

              <Style.DownloadButton onClick={getCommercialOfferFile}>
                <Download fill={tokens.colors.vibin} width={24} height={24} />

                <Style.DownloadButtonDescription>
                  <FormattedMessage id="smb_acquisition_commercial_offer.page.media.proposal_download" />
                </Style.DownloadButtonDescription>
              </Style.DownloadButton>
            </Col>

            <Col xxs={12} xl-start={8} xl={4}>
              <SummaryCard contact={contact} />
            </Col>
          </Style.WrapperTitle>
        </Style.Container>
      </Style.Header>

      <Style.NextSteps>
        <Style.Container>
          <Row>
            <Col xxs={12} xl-start={2} xl={10}>
              <Style.SectionTitle>
                <FormattedMessage
                  id="smb_acquisition_thank_you.success.next_steps.title"
                  values={{
                    span: (...chunks) => <span>{chunks}</span>,
                  }}
                />
              </Style.SectionTitle>
            </Col>
          </Row>

          <Row>
            <Col xxs={12} xl-start={2} xl={8}>
              <Style.NextStepItem>
                <Style.NextStepIcon>1</Style.NextStepIcon>

                <Style.NextStepDescription>
                  <FormattedMessage
                    id="smb_acquisition_thank_you.success.next_steps.step_1.title"
                    values={{
                      strong: (...chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                </Style.NextStepDescription>
              </Style.NextStepItem>
            </Col>
          </Row>

          <Row>
            <Col xxs={12} xl-start={2} xl={8}>
              <Style.NextStepItem>
                <Style.NextStepIcon>2</Style.NextStepIcon>

                <Style.NextStepDescription>
                  <FormattedMessage
                    id="smb_acquisition_thank_you.success.next_steps.step_2.title"
                    values={{
                      strong: (...chunks) => <strong>{chunks}</strong>,
                      a: (...chunks) => (
                        <a href="https://hs.gympass.com/hubfs/SMB_files/employees_database_sample.csv">
                          {chunks}
                        </a>
                      ),
                    }}
                  />
                </Style.NextStepDescription>
              </Style.NextStepItem>
            </Col>
          </Row>

          <Row>
            <Col xxs={12} xl-start={2} xl={8}>
              <Style.NextStepItem>
                <Style.NextStepIcon>3</Style.NextStepIcon>

                <Style.NextStepDescription>
                  <FormattedMessage
                    id="smb_acquisition_thank_you.success.next_steps.step_3.title"
                    values={{
                      strong: (...chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                </Style.NextStepDescription>
              </Style.NextStepItem>
            </Col>
          </Row>
        </Style.Container>
      </Style.NextSteps>

      <Style.Payment>
        <Style.Container>
          <Row>
            <Col xxs={12} xl-start={2} xl={10}>
              <Style.SectionTitle>
                <FormattedMessage id="smb_acquisition_thankyou.success.how_payment_works.title" />
              </Style.SectionTitle>

              <Style.PaymentDescriptionWrapper>
                <Style.PaymentDescription>
                  <FormattedMessage
                    id="smb_acquisition_thankyou.success.how_payment_works.paragraph_1"
                    values={{
                      strong: (...chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                </Style.PaymentDescription>

                <Style.PaymentDescription>
                  <FormattedMessage
                    id="smb_acquisition_thankyou.success.how_payment_works.paragraph_2"
                    values={{
                      strong: (...chunks) => <strong>{chunks}</strong>,
                    }}
                  />
                </Style.PaymentDescription>
              </Style.PaymentDescriptionWrapper>
            </Col>
          </Row>
        </Style.Container>
      </Style.Payment>

      <Footer links={links} social={social} company="Gympass B.V." />
      <YelpPixel
        revenue={contact.proposalValue}
        orderid={contact.id}
        country={country}
      />
    </>
  )
}
